import React, { useContext, useMemo } from 'react'
import {
    Grid,
    Button,
    Link,
    Box,
    Card,
    CardContent,
    Typography,
} from '@mui/material'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import { signin } from '../api'
import { SignInRequest } from '../model'
import { AppContext } from '../context'
import { translate } from '../i18n'
import { ExternSurface } from '../components'
import { FeedbackContext } from '../feedback'
import { AnyInput, useValidatedForm } from '../anyform'
import { PasswordValidator } from '../validators'

export default function SignInForm() {
    const history = useHistory()
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)
    const { register, bind, isValid } = useValidatedForm<SignInRequest>()

    const validators = useMemo(() => [new PasswordValidator()], [])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()

        const request = bind()
        signin(request)
            .then((response) => {
                switch (response.status) {
                    case 'SIGNED_IN':
                        context.onSignedIn()
                        break
                    case 'TENANT_VERIFICATION':
                        history.push(`/signup/${response.tenant_signup_code!}`)
                        break
                    case 'REQUIRED_2FA':
                        history.push(`/signin/${response.signin_2fa_code!}`)
                        break
                    default:
                        break
                }
            })
            .catch((error) => {
                feedbackContext.showError(error.message)
            })
            .finally(() => {
                feedbackContext.closeBackdrop()
            })
    }

    return (
        <ExternSurface
            title={translate('auth.signin.title') as string}
            showTitle
        >
            <form autoComplete="off" noValidate onSubmit={onSubmit}>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid item xs={12}>
                        <AnyInput
                            type="email"
                            name="email"
                            label={translate('users.email') as string}
                            required
                            hiddenLabel
                            register={register}
                        />
                        <AnyInput
                            type="password"
                            name="password"
                            label={translate('users.password') as string}
                            required
                            hiddenLabel
                            register={register}
                            validators={validators}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box my={2}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                            >
                                {translate('buttons.signin')}
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs="auto">
                        <Link component={RouterLink} to="/recover-password">
                            <small>{translate('buttons.recover')}</small>
                        </Link>
                    </Grid>
                </Grid>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid item xs={12} sx={{ mt: 3 }}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1">
                                    {translate('auth.signin.security.title')}
                                </Typography>
                                <Typography variant="body2">
                                    {translate('auth.signin.security.text')}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </form>
        </ExternSurface>
    )
}
